import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Search, Star, MapPin, DollarSign, Filter } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface Trainer {
  id: string;
  name: string;
  bio: string | null;
  profile_image_url: string | null;
  hourly_rate: number;
  rating_average: number;
  total_reviews: number;
  experience_level: string;
  service_areas: string[] | null;
}

export default function TrainerDiscoveryPage() {
  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterExperience, setFilterExperience] = useState<string>('all');
  const [filterPriceRange, setFilterPriceRange] = useState<string>('all');

  const loadTrainers = useCallback(async () => {
    try {
      let query = supabase
        .from('trainers')
        .select('*')
        .eq('is_active', true)
        .eq('verification_status', 'verified');

      if (filterExperience !== 'all') {
        query = query.eq('experience_level', filterExperience);
      }

      const { data, error } = await query;

      if (error) throw error;

      let filteredData = data || [];

      // Filter by price range
      if (filterPriceRange !== 'all') {
        const [min, max] = filterPriceRange.split('-').map(Number);
        filteredData = filteredData.filter((trainer) => {
          const rate = trainer.hourly_rate || 0;
          if (max) {
            return rate >= min && rate <= max;
          }
          return rate >= min;
        });
      }

      // Sort by rating
      filteredData.sort((a, b) => (b.rating_average || 0) - (a.rating_average || 0));

      setTrainers(filteredData as Trainer[]);
    } catch (error) {
      console.error('Error loading trainers:', error);
    } finally {
      setLoading(false);
    }
  }, [filterExperience, filterPriceRange]);

  useEffect(() => {
    loadTrainers();
  }, [loadTrainers]);

  const filteredTrainers = trainers.filter((trainer) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      trainer.name.toLowerCase().includes(query) ||
      trainer.bio?.toLowerCase().includes(query) ||
      trainer.service_areas?.some((area) => area.toLowerCase().includes(query))
    );
  });

  if (loading) {
    return (
      <>
        <Header />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-600">Loading trainers...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Find Your Perfect Trainer</h1>
            <p className="mt-2 text-gray-600">
              Browse certified trainers and book personalized sessions
            </p>
          </div>

          {/* Search and Filters */}
          <div className="mb-8 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search trainers by name, specialty, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-3 pl-10 pr-4 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>

            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-gray-400" />
                <span className="text-sm font-semibold text-gray-700">Filters:</span>
              </div>
              <select
                value={filterExperience}
                onChange={(e) => setFilterExperience(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="all">All Experience Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="expert">Expert</option>
              </select>
              <select
                value={filterPriceRange}
                onChange={(e) => setFilterPriceRange(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="all">All Prices</option>
                <option value="0-50">$0 - $50/hr</option>
                <option value="50-100">$50 - $100/hr</option>
                <option value="100-150">$100 - $150/hr</option>
                <option value="150-999">$150+/hr</option>
              </select>
            </div>
          </div>

          {/* Trainers Grid */}
          {filteredTrainers.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
              <p className="text-lg font-semibold text-gray-900">No trainers found</p>
              <p className="mt-2 text-sm text-gray-600">
                Try adjusting your search or filters
              </p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredTrainers.map((trainer) => (
                <Link
                  key={trainer.id}
                  to={`/trainer/${trainer.id}`}
                  className="group rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition hover:shadow-md"
                >
                  <div className="mb-4 flex items-start gap-4">
                    <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-full bg-gray-200">
                      {trainer.profile_image_url ? (
                        <img
                          src={trainer.profile_image_url}
                          alt={trainer.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-blue-100 text-blue-600">
                          <span className="text-xl font-semibold">
                            {trainer.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600">
                        {trainer.name}
                      </h3>
                      <div className="mt-1 flex items-center gap-2">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm font-semibold text-gray-700">
                          {trainer.rating_average?.toFixed(1) || '0.0'}
                        </span>
                        <span className="text-sm text-gray-500">
                          ({trainer.total_reviews || 0} reviews)
                        </span>
                      </div>
                    </div>
                  </div>
                  {trainer.bio && (
                    <p className="mb-4 line-clamp-2 text-sm text-gray-600">{trainer.bio}</p>
                  )}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Experience:</span>
                      <span className="font-semibold capitalize text-gray-900">
                        {trainer.experience_level}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Rate:</span>
                      <span className="flex items-center gap-1 font-semibold text-gray-900">
                        <DollarSign className="h-4 w-4" />
                        {trainer.hourly_rate?.toFixed(2) || '0.00'}/hr
                      </span>
                    </div>
                    {trainer.service_areas && trainer.service_areas.length > 0 && (
                      <div className="flex items-start gap-2 text-sm">
                        <MapPin className="h-4 w-4 flex-shrink-0 text-gray-400" />
                        <span className="text-gray-600">
                          {trainer.service_areas.slice(0, 2).join(', ')}
                          {trainer.service_areas.length > 2 && '...'}
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
